import { Language } from "@/Common/Models/Domain/Language";
import type { Locale } from "../Model/Domain";
import { LANG_STORAGE_KEY, LOCALE_STORAGE_SERVICE } from "../Model/Constants";

import useI18nStore from "../Store";
import ActivateLocaleActionHandler from "./ActivateLocale";
import localeCacheService from "../Services/LocaleCacheService";

import { danger } from "@/Utils/Debug";
import { normalizeLanguage } from "@/Utils/Language";
import { hasValue } from "@/Utils/Objects";
import { LocalStorageService } from "@/Services/LocalStorageService";

const localeLocalStorageService = new LocalStorageService(LOCALE_STORAGE_SERVICE);

interface PendingLocaleSelection {
  locale: Language;
  force: boolean;
  resolve: (selected: boolean) => void;
}

let isSelectingLocale = false;
let localeBeingSelected: Language | undefined;
let pendingLocaleSelection: PendingLocaleSelection | undefined;

function getCachedLocale(locale: Language): Locale | undefined {
  const i18nStore = useI18nStore();
  const memoryLocale = i18nStore.locales.get(locale);

  if (hasValue(memoryLocale)) return memoryLocale;

  const persistedLocale = localeCacheService.get(locale);

  if (hasValue(persistedLocale)) {
    i18nStore.locales.set(locale, persistedLocale);
  }

  return persistedLocale;
}

async function fetchLocale(locale: Language): Promise<Locale | undefined> {
  const i18nStore = useI18nStore();

  try {
    const fetchedLocale = await i18nStore.fetchLocale(locale);

    if (!hasValue(fetchedLocale)) {
      danger("FETCH LOCALE EMPTY RESPONSE", locale);
    }

    return fetchedLocale;
  } catch (error) {
    danger("FETCH LOCALE ERROR", error);

    return;
  }
}

function canActivateRefreshedLocale(locale: Language): boolean {
  const i18nStore = useI18nStore();

  return (
    i18nStore.locale === locale &&
    !hasValue(pendingLocaleSelection) &&
    (!isSelectingLocale || localeBeingSelected === locale)
  );
}

async function revalidateLocale(locale: Language, force: boolean): Promise<void> {
  const i18nStore = useI18nStore();

  try {
    const needUpdate = force || (await i18nStore.checkLocaleVersion(locale));

    if (!needUpdate) return;

    const fetchedLocale = await fetchLocale(locale);

    if (hasValue(fetchedLocale) && canActivateRefreshedLocale(locale)) {
      ActivateLocaleActionHandler(fetchedLocale);
    }
  } catch (error) {
    danger("REVALIDATE LOCALE ERROR", locale, error);
  }
}

async function runLocaleSelection(locale: Language, force: boolean): Promise<boolean> {
  const i18nStore = useI18nStore();
  const localeToSelect = normalizeLanguage(locale) ?? Language.ENGLISH;

  if (i18nStore.locale === localeToSelect && !force) {
    localeLocalStorageService.set(LANG_STORAGE_KEY, localeToSelect);

    return false;
  }

  i18nStore.isLoading = true;

  try {
    const cachedLocale = getCachedLocale(localeToSelect);

    if (hasValue(cachedLocale)) {
      if (hasValue(pendingLocaleSelection)) return false;

      if (i18nStore.locale !== localeToSelect) {
        ActivateLocaleActionHandler(cachedLocale);
      }

      if (force) {
        await revalidateLocale(localeToSelect, true);
      } else {
        void revalidateLocale(localeToSelect, false);
      }

      return i18nStore.locale === localeToSelect;
    }

    const fetchedLocale = await fetchLocale(localeToSelect);

    if (hasValue(pendingLocaleSelection) || !hasValue(fetchedLocale)) return false;

    ActivateLocaleActionHandler(fetchedLocale);

    return i18nStore.locale === localeToSelect;
  } catch (error) {
    danger("SELECT LOCALE ERROR", error);

    return false;
  } finally {
    i18nStore.isLoading = false;
  }
}

async function processLocaleSelection(locale: Language, force: boolean): Promise<boolean> {
  isSelectingLocale = true;
  localeBeingSelected = locale;

  try {
    return await runLocaleSelection(locale, force);
  } catch (error) {
    danger("PROCESS LOCALE SELECTION ERROR", error);

    return false;
  } finally {
    isSelectingLocale = false;
    localeBeingSelected = undefined;

    const nextSelection = pendingLocaleSelection;

    pendingLocaleSelection = undefined;

    if (hasValue(nextSelection)) {
      void processLocaleSelection(nextSelection.locale, nextSelection.force).then(nextSelection.resolve);
    }
  }
}

function SelectLocaleActionHandler(locale: Language, force: boolean = false): Promise<boolean> {
  if (!isSelectingLocale) {
    return processLocaleSelection(locale, force);
  }

  return new Promise((resolve) => {
    pendingLocaleSelection?.resolve(false);
    pendingLocaleSelection = { locale, force, resolve };
  });
}

export default SelectLocaleActionHandler;
